import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useRef
import { supabase } from '../supabaseClient';
import Hero from './Hero';
import whyChooseUsImg from '../assets/anothereclass/image00013.jpeg';
import clientBgImg from '../assets/anothereclass/image00014.jpeg';
import Services from '../components/Services';
import { motion, AnimatePresence } from 'framer-motion';

import { 
  ChevronRight, Quote, HeartHandshake, Users, Sparkles, 
  ChevronLeft, Star, Zap, X, ShieldCheck, CheckCircle2, 
  User, Phone, Calendar, Mail  
} from 'lucide-react';

const Home = () => {
  const [activeFeedback, setActiveFeedback] = useState(0); 
  const [testimonials, setTestimonials] = useState([]); 
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null); // Ref for fleet scrolling

  // Modal & Booking States
  const [selectedCar, setSelectedCar] = useState(null);
  const [bookingStep, setBookingStep] = useState('details'); 
  const [formData, setFormData] = useState({ name: '', phone: '', date: '' });
  const [name, setName] = useState("");

  // 1. Data Fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: testData } = await supabase
          .from('testimonials')
          .select('*')
          .order('order_index', { ascending: true });
        
        const { data: carData } = await supabase
          .from('cars')
          .select('*')
          .order('display_order', { ascending: true });

        if (testData) setTestimonials(testData);
        if (carData) setCars(carData);
      } catch (error) {
        console.error('Error fetching data:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fleet Scroll Logic
  const scrollFleet = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const nextFeedback = useCallback(() => {
    setTestimonials((prevData) => {
      if (prevData.length === 0) return prevData;
      setActiveFeedback((current) => (current + 1) % prevData.length);
      return prevData;
    });
  }, []);

  const prevFeedback = () => {
    setActiveFeedback((current) => 
      current === 0 ? testimonials.length - 1 : current - 1
    );
  };

  useEffect(() => {
    if (testimonials.length > 0) {
      const timer = setInterval(() => { nextFeedback(); }, 5000);
      return () => clearInterval(timer);
    }
  }, [testimonials.length, nextFeedback]);

  const closeModal = () => {
    setSelectedCar(null);
    setBookingStep('details');
    setFormData({ name: '', phone: '', date: '' });
  };

  // Fixed: Removed unused handleBookingSubmit to clear ESLint warning

  return (
    <main className="bg-white antialiased relative">
      <Hero />

      {/* --- COMPACT FLEET CAROUSEL WITH ARROWS --- */}
      <section className="py-12 bg-white overflow-hidden relative group/fleet">
        <div className="max-w-7xl mx-auto px-6 mb-8 text-center">
          <p className="text-[#1C4D8D] font-bold tracking-[0.2em] uppercase text-[9px] mb-2 opacity-80">Our Premium Fleet</p>
          <h2 className="text-2xl md:text-4xl font-black text-slate-900 uppercase tracking-tighter">
            Excellence in <span className="text-[#1C4D8D] italic">Mobility.</span>
          </h2>
        </div>

        {/* Navigation Arrows */}
        <button 
          onClick={() => scrollFleet('left')}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 bg-white/80 p-3 shadow-lg border border-slate-100 opacity-0 group-hover/fleet:opacity-100 transition-opacity hidden md:block"
        >
          <ChevronLeft size={24} className="text-slate-800" />
        </button>
        <button 
          onClick={() => scrollFleet('right')}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 bg-white/80 p-3 shadow-lg border border-slate-100 opacity-0 group-hover/fleet:opacity-100 transition-opacity hidden md:block"
        >
          <ChevronRight size={24} className="text-slate-800" />
        </button>

        <div className="flex overflow-x-auto no-scrollbar scroll-smooth" ref={scrollRef}>
          <div className="flex animate-scroll gap-4 py-2">
            {[...cars, ...cars].map((car, index) => (
              <div key={index} className="flex-shrink-0 w-[260px] md:w-[320px] bg-white border border-slate-100 rounded-none overflow-hidden shadow-sm hover:shadow-md transition-all duration-500 group/card">
                <div className="h-40 md:h-44 bg-gray-50/50 p-4 flex items-center justify-center relative">
                  <img src={car.image_url} alt={car.name} className="w-full h-full object-contain transition-transform duration-700 group-hover/card:scale-105" />
                  <div className="absolute top-2 right-2 bg-white/90 px-1.5 py-0.5 flex items-center gap-1 rounded-none border border-slate-100">
                    <Star size={8} className="text-yellow-500 fill-yellow-500" />
                    <span className="font-black text-[8px] text-slate-800">{car.rating || '5.0'}</span>
                  </div>
                </div>

                <div className="p-4 border-t border-slate-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-[#1C4D8D] text-[8px] font-black uppercase tracking-widest mb-0.5">{car.category}</p>
                      <h3 className="text-[13px] md:text-sm font-bold text-slate-900 uppercase truncate max-w-[150px]">{car.name}</h3>
                    </div>
                    <div className="flex items-center gap-1 text-slate-400 text-[9px] font-bold">
                      <Users size={12} /> {car.seats}
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <button 
                      onClick={() => setSelectedCar(car)}
                      className="flex items-center justify-center gap-2 bg-[#1C4D8D] text-white px-4 py-2 text-[8px] font-black uppercase tracking-widest hover:bg-blue-500 transition-all rounded-none shadow-sm active:scale-95"
                    >
                      <Zap size={10} fill="currentColor"/> Book Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- INTEGRATED BOOKING MODAL --- */}
      {selectedCar && (
        <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-0 md:p-4 overflow-hidden">
          <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-md" onClick={closeModal}></div>
          <div className="relative bg-white w-full max-w-5xl rounded-none md:rounded-none flex flex-col md:flex-row h-full max-h-[95vh] md:h-auto shadow-2xl overflow-y-auto">
            <button onClick={closeModal} className="absolute top-4 right-4 z-[100] bg-white/90 p-2 rounded-full border border-gray-200 shadow-sm">
              <X size={20} className="text-slate-900" />
            </button>
            
            <div className={`relative w-full md:w-1/2 min-h-[250px] md:h-auto flex items-center justify-center p-6 ${bookingStep === 'success' ? 'bg-green-500' : 'bg-gray-50/50'}`}>
              {bookingStep !== 'success' ? (
                <img src={selectedCar.image_url} alt={selectedCar.name} className="relative z-10 w-full max-h-[200px] md:max-h-full object-contain drop-shadow-xl" />
              ) : (
                <div className="text-white text-center">
                  <CheckCircle2 size={50} className="mx-auto mb-4" />
                  <h2 className="text-xl font-black uppercase">Confirmed!</h2>
                </div>
              )}
            </div>

            <div className="w-full md:w-1/2 p-6 md:p-14 bg-white overflow-y-auto">
              <div className="pb-20 md:pb-0">
                {bookingStep === 'details' && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 text-blue-600 font-black text-[10px] uppercase tracking-widest"><ShieldCheck size={16} /> Premium Fleet</div>
                    <h2 className="text-3xl font-black text-slate-900 mb-2 uppercase leading-none">{selectedCar.name}</h2>
                    <p className="text-slate-500 text-sm mb-6 italic">"{selectedCar.description || 'Professional chauffeur service.'}"</p>
                    <button onClick={() => setBookingStep('form')} className="w-full bg-blue-600 text-white py-5 font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl">
                      Reserve Now <ChevronRight size={18}/>
                    </button>
                  </div>
                )}

                {bookingStep === 'form' && (
                  <>
                    {/* FIXED: Added title to iframe for ESLint accessibility warning */}
                    <iframe name="hidden_iframe_fleet" title="Form Submission" style={{ display: "none" }}></iframe>
                    <form
                      action="https://formsubmit.co/enquiry@expresstravelcorp.com"
                      method="POST"
                      target="hidden_iframe_fleet"
                      onSubmit={() => {
                        setTimeout(() => setBookingStep('success'), 1000);
                      }} className="space-y-5">
                      <input type="hidden" name="_subject" value={`New Enquiry from ${name}`} />
                      <input type="hidden" name="_captcha" value="false" />
                      <input type="hidden" name="_template" value="table" />
                      <input type="text" name="_honey" style={{ display: "none" }} />
                      <h3 className="text-xl font-black text-slate-900 uppercase">Booking Details</h3>
                      <div className="space-y-4">
                        <div className="relative"><User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input required type="text" name='name' placeholder="Full Name" className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 text-sm" onChange={(e) => setName(e.target.value)} /></div>
                        <div className="relative"><Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input required type="tel" name='phone' placeholder="Phone Number" className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 text-sm" onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
                        <div className="relative"><Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input required type="text" name='email' placeholder="Email Address" className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 text-sm" onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
                        <div className="relative"><Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} /><input required type="date" name='date' max ="9999-12-31" className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 text-sm" onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={() => setBookingStep('details')} className="px-6 py-4 bg-slate-100 font-bold text-slate-500 uppercase text-[10px]">Back</button>
                        <button type="submit" className="flex-1 bg-slate-900 text-white py-4 font-black text-[10px] uppercase tracking-widest">Confirm Booking</button>
                      </div>
                    </form>
                  </>
                )}

                {bookingStep === 'success' && (
                  <div className="text-center py-6">
                    <h3 className="text-2xl font-black text-slate-900 mb-3 uppercase">Thank you!</h3>
                    <p className="text-slate-500 text-sm mb-8 leading-relaxed">Our concierge will contact you at <span className="text-slate-900 font-bold">{formData.phone}</span> shortly.</p>
                    <button onClick={closeModal} className="w-full bg-slate-900 text-white py-4 font-black text-[10px] uppercase">Close</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- STATS SECTION --- */}
      <section className="bg-blue-50 py-12 px-6 border-y border-blue-100 rounded-xl mx-4 md:mx-10 shadow-sm">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 divide-x-0 md:divide-x md:divide-blue-200">
            <div className="text-center">
              <h2 className="text-5xl font-black text-[#001233] mb-1 tracking-tighter">1500+</h2>
              <p className="text-blue-600 font-extrabold text-[10px] uppercase tracking-widest">Fleet Vehicles</p>
            </div>
            <div className="text-center">
              <h2 className="text-5xl font-black text-[#001233] mb-1 tracking-tighter">25+</h2>
              <p className="text-blue-600 font-extrabold text-[10px] uppercase tracking-widest">Years Experience</p>
            </div>
            <div className="text-center">
              <h2 className="text-5xl font-black text-[#001233] mb-1 tracking-tighter">120+</h2>
              <p className="text-blue-600 font-extrabold text-[10px] uppercase tracking-widest">Cities Covered</p>
            </div>
            <div className="text-center">
              <h2 className="text-5xl font-black text-[#001233] mb-1 tracking-tighter">1000+</h2>
              <p className="text-blue-600 font-extrabold text-[10px] uppercase tracking-widest">Happy Clients</p>
            </div>
            <div className="text-center">
              <h2 className="text-5xl font-black text-[#001233] mb-1 tracking-tighter">24/7</h2>
              <p className="text-blue-600 font-extrabold text-[10px] uppercase tracking-widest">Availability</p>
            </div>
          </div>
        </div>
      </section>

      <Services />

      {/* --- WHY CHOOSE US --- */}
      <section className="relative h-auto py-8 lg:h-[450px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: `url(${whyChooseUsImg})` }}>
          <div className="absolute inset-0 bg-black/65"></div> 
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center text-white">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter mb-8">Why Choose Us?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex flex-col items-center">
              <ChevronRight className="w-8 h-8 text-blue-400 mb-2" />
              <h3 className="text-[15px] font-black uppercase mb-3 border-b border-white/20 pb-2 w-full tracking-tight">Various Services</h3>
              <ul className="text-[12px] text-gray-200 uppercase space-y-1 opacity-90"><li>Airport Transfers</li><li>Corporate Services</li><li>Conference & MICE</li></ul>
            </div>
            <div className="flex flex-col items-center">
              <HeartHandshake className="w-8 h-8 text-blue-400 mb-2" />
              <h3 className="text-[15px] font-black uppercase mb-3 border-b border-white/20 pb-2 w-full tracking-tight">Value Added</h3>
              <ul className="text-[12px] text-gray-200 uppercase space-y-1 opacity-90"><li>24/7 Availability</li><li>Mobile App</li><li>Safety Kits</li></ul>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 text-blue-400 mb-2" />
              <h3 className="text-[15px] font-black uppercase mb-3 border-b border-white/20 pb-2 w-full tracking-tight">Driver Profile</h3>
              <ul className="text-[12px] text-gray-200 uppercase space-y-1 opacity-90"><li>Strict Punctuality</li><li>Well-Groomed</li><li>Local Knowledge</li></ul>
            </div>
            <div className="flex flex-col items-center">
              <Sparkles className="w-8 h-8 text-blue-400 mb-2" />
              <h3 className="text-[15px] font-black uppercase mb-3 border-b border-white/20 pb-2 w-full tracking-tight">Salient Features</h3>
              <ul className="text-[12px] text-gray-200 uppercase space-y-1 opacity-90"><li>Hotel Booking</li><li>All Credit Cards</li><li>customer Support</li></ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- CLIENT FEEDBACK SECTION --- */}
      {testimonials.length > 0 && (
        <section className="py-12 px-6 overflow-hidden relative bg-white">
          <div className="absolute inset-0 bg-cover bg-center z-0 opacity-5" style={{ backgroundImage: `url(${clientBgImg})` }}></div>
          <div className="max-w-2xl mx-auto text-center relative z-10">
            <h2 className="text-2xl md:text-3xl font-black text-slate-900 uppercase mb-8 tracking-tighter">
              What Our <span className="text-[#1C4D8D] italic">Clients Say</span>
            </h2>
            
            <div className="relative flex items-center justify-center">
              <button 
                onClick={prevFeedback} 
                className="absolute left-0 md:-left-16 z-20 p-2 text-[#1C4D8D] border border-[#1C4D8D]/10 rounded-full hover:bg-[#1C4D8D] hover:text-white transition-all bg-white/50"
              >
                <ChevronLeft size={20}/>
              </button>

              <div className="w-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeFeedback}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.4 }}
                    className="bg-slate-50 p-6 md:p-10 rounded-none shadow-lg text-center border border-slate-100 relative min-h-[200px] flex flex-col justify-center mx-auto"
                  >
                    <Quote className="text-[#1C4D8D] mx-auto mb-4 opacity-20" size={32} />
                    <p className="text-slate-600 text-sm md:text-base leading-relaxed italic font-medium mb-5">
                      "{testimonials[activeFeedback]?.text}"
                    </p>
                    <div className="w-10 h-0.5 bg-blue-500 mx-auto mb-3"></div>
                    <p className="font-black text-[#1C4D8D] uppercase tracking-tighter text-md">
                      {testimonials[activeFeedback]?.name}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              <button 
                onClick={nextFeedback} 
                className="absolute right-0 md:-right-16 z-20 p-2 text-[#1C4D8D] border border-[#1C4D8D]/10 rounded-full hover:bg-[#1C4D8D] hover:text-white transition-all bg-white/50"
              >
                <ChevronRight size={20}/>
              </button>
            </div>
            
            <div className="flex justify-center gap-1.5 mt-6">
                {testimonials.map((_, index) => (
                    <div 
                        key={index} 
                        onClick={() => setActiveFeedback(index)}
                        className={`h-1 cursor-pointer transition-all duration-300 ${index === activeFeedback ? 'w-6 bg-[#1C4D8D]' : 'w-1.5 bg-slate-300'}`}
                    />
                ))}
            </div>
          </div>
        </section>
      )}

      {loading && (
        <div className="py-8 text-center text-[#1C4D8D] font-bold text-sm">
          Loading Experiences...
        </div>
      )}

      {/* CSS for Animation & Scrollbars */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          display: flex;
          animation: scroll 180s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
        /* Hide scrollbar for Chrome, Safari and Opera */
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        /* Hide scrollbar for IE, Edge and Firefox */
        .no-scrollbar {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
      `}</style>
    </main>
  );
};
export default Home;